import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

/* Регионы и их валюта. Символ/валюта известны на клиенте всегда (не зависят
   от админ-дефолтов), а конкретные цены приходят из pricing[region]. */
export const REGIONS = {
  ru: { code: 'ru', currency: 'RUB', symbol: '₽',  locale: 'ru-RU', label: 'Россия' },
  by: { code: 'by', currency: 'BYN', symbol: 'Br', locale: 'ru-BY', label: 'Беларусь' },
  kz: { code: 'kz', currency: 'KZT', symbol: '₸',  locale: 'ru-KZ', label: 'Казахстан' },
};

/* Определение региона. Приоритет:
   1) путь URL (/by, /kz),
   2) регион залогиненного пользователя (localStorage user.region),
   3) ранее сохранённый регион (localStorage 'region'),
   4) ru по умолчанию. */
export function detectRegion() {
  try {
    const seg = (window.location.pathname.split('/')[1] || '').toLowerCase();
    if (REGIONS[seg] && seg !== 'ru') return seg;
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u && u.region && REGIONS[u.region]) return u.region;
    const stored = localStorage.getItem('region');
    if (stored && REGIONS[stored]) return stored;
  } catch (_) { /* ignore */ }
  return 'ru';
}

/* Цена/валюта для региона из объекта pricing (admin-defaults).
   Цена: pricing[region].price → legacy pricing.price → 2499.
   Валюта/символ: из REGIONS (всегда известны). */
export function priceFor(pricing, region) {
  const meta = REGIONS[region] || REGIONS.ru;
  const rp = (pricing && pricing[region]) || {};
  const price = Number(rp.price) || Number(pricing && pricing.price) || 2499;
  const originalPrice = Number(rp.originalPrice) || Number(pricing && pricing.originalPrice) || 3199;
  return {
    price,
    originalPrice,
    currency: meta.currency,
    symbol: meta.symbol,
    locale: meta.locale,
    /* Код валюты для Робокассы (OutSumCurrency); пусто для ru (рубли). */
    outSumCurrency: (rp.outSumCurrency != null ? rp.outSumCurrency : (region === 'ru' ? '' : meta.currency)),
  };
}

/* Удобный форматтер: "2 499 ₽". */
export function formatPrice(value, region) {
  const meta = REGIONS[region] || REGIONS.ru;
  return `${Number(value || 0).toLocaleString(meta.locale)} ${meta.symbol}`;
}

const RegionContext = createContext({ region: 'ru', setRegion: () => {}, ...REGIONS.ru });

export function RegionProvider({ children }) {
  const auth = useAuth();
  const user = auth && auth.user;
  const [region, setRegionState] = useState(detectRegion);

  const urlRegion = () => {
    const seg = (window.location.pathname.split('/')[1] || '').toLowerCase();
    return (REGIONS[seg] && seg !== 'ru') ? seg : null;
  };

  /* Если зашли на /by или /kz — запоминаем регион, чтобы он дожил до
     чат-бота и оплаты при SPA-навигации. URL имеет высший приоритет. */
  useEffect(() => {
    const seg = urlRegion();
    if (seg) {
      try { localStorage.setItem('region', seg); } catch (_) {}
      if (seg !== region) setRegionState(seg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Регион ЗАЛОГИНЕННОГО пользователя авторитетен (из user.region) — кроме
     случая явного входа через /by, /kz. Реагируем на смену пользователя,
     иначе после входа под KZ-юзером цена «залипала» в ₸ и для других. */
  useEffect(() => {
    if (urlRegion()) return; // URL-выбор не перебиваем
    const ur = user && user.region;
    if (ur && REGIONS[ur]) {
      try { localStorage.setItem('region', ur); } catch (_) {}
      if (ur !== region) setRegionState(ur);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.region, user && user.id]);

  const setRegion = (r) => {
    const next = REGIONS[r] ? r : 'ru';
    try { localStorage.setItem('region', next); } catch (_) {}
    setRegionState(next);
  };

  const meta = REGIONS[region] || REGIONS.ru;
  return (
    <RegionContext.Provider value={{ region, setRegion, ...meta }}>
      {children}
    </RegionContext.Provider>
  );
}

export const useRegion = () => useContext(RegionContext);
